/* eslint-disable @typescript-eslint/no-empty-function */

/* eslint-disable @typescript-eslint/ban-types */
import React, { useCallback, useEffect, useRef, useState } from "react";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import styles from "./carousel.module.scss";
import ArrowIcon from "icons/ArrowIcon";
import wrapperHOC from "@Utils/wrapperHOC";

interface Props {
  slideList: any;
  handleChangeSlide?: (pos: number) => void;
  activePos: number;
  activeSlideDataLabel?: string | number;
  className?: string;
  autoSlide?: boolean;
  showNavSlider?: boolean;
  sliderContainerClassName?: string;
  noInfinite?: boolean;
  roundedCorner?: boolean;
  navSliderSize?: keyof typeof arrowSize;
  navSliderColor?: "transparent" | "";
  showNavOnHover?: boolean;
  overflowHide?: boolean;
  isDraggableInDesktop?: boolean;
  delay?: number;
  leftNavSliderClassname?: string;
  rightNavSliderClassname?: string;
  lastClassName?: string;
  carouselRef?: any;
  slideClassName?: string;
  noLazyLoad?: boolean;
  firstDelay?: number;
  arrowColor?: string;
  pause?: boolean;
  showModal?: boolean;
  renderMultipleNextSlide?: boolean;
  disableEdgeScrolling?: boolean;
  showPartialSlidesOnEdge?: boolean;
  leftArrowClassName?: string;
  rightArrowClassName?: string;
  arrowStrokeWidth?: number;
  style?: any;
  keypressEnabled?: boolean;
  sliderWrapperClassName?: string;
  customArrowIcon?: React.ReactNode;
}

const arrowSize = {
  extraSmall: "10px",
  small: "16px",
  large: "22px",
};
/*
  add height in given className
*/
const Carousel: React.FC<Props> = (props) => {
  const {
    slideList,
    handleChangeSlide,
    activePos,
    className,
    autoSlide,
    sliderContainerClassName,
    showNavSlider,
    noInfinite,
    roundedCorner,
    navSliderSize = "small",
    navSliderColor,
    showNavOnHover,
    isDraggableInDesktop,
    leftNavSliderClassname,
    rightNavSliderClassname,
    activeSlideDataLabel,
    overflowHide,
    lastClassName,
    carouselRef,
    slideClassName,
    noLazyLoad,
    firstDelay,
    delay,
    arrowColor,
    pause,
    showModal,
    renderMultipleNextSlide,
    disableEdgeScrolling,
    showPartialSlidesOnEdge,
    leftArrowClassName,
    rightArrowClassName,
    arrowStrokeWidth,
    style = {},
    keypressEnabled,
    sliderWrapperClassName,
    customArrowIcon,
  } = props;
  const [activeSlide, setActiveSlide] = useState(activePos);
  const [isTransition, setIsTransition] = useState(false);
  const [hovered, setIsHovered] = useState(false);
  const isWrapping = useRef(false);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const isMouseDown = useRef(false);
  const isDragEndStart = useRef(false);
  const diffPercentageRef = useRef(0);
  const posRef = useRef(0);
  const slideContainerRef = React.createRef<HTMLDivElement>();
  const threshold = 20;
  const totalSlide = slideList.length;
  const autoSlideStarted = useRef(false);
  const secondCountRef = useRef(0);
  const shouldStartTransitioningRef = useRef("init");
  // delay for auto slide in second
  const delays = delay || 5;
  const timerId = useRef<any>(null);

  const hasMultipleSlides = totalSlide > 1;
  const shouldShowNav = hasMultipleSlides && (showNavOnHover ? hovered : showNavSlider);

  useEffect(() => {
    if (isWrapping.current) return;
    if (activePos !== activeSlide) {
      setIsTransition(true);
      setActiveSlide(activePos);

      setTimeout(() => {
        setIsTransition(false);
      }, 500);
    }
  }, [activePos]);

  useEffect(() => {
    if (autoSlide && !pause) {
      trackTimeForAutoSlide();
    }
    return () => {
      clearInterval(timerId.current);
    };
  }, [activeSlide, autoSlide]);

  // The Keypress Event Handler
  const changeChild = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        slideLeft();
      } else if (e.key === "ArrowRight") {
        slideRight();
      }
    },
    [slideList.length, activeSlide],
  );

  useEffect(() => {
    if (!hasMultipleSlides) return undefined;
    slideContainerRef.current?.addEventListener("touchstart", (e) => onDragStartTouch(e), {
      passive: true,
    });
    return () =>
      slideContainerRef.current?.removeEventListener("touchstart", (e) => onDragStartTouch(e));
  }, [hasMultipleSlides]);

  useEffect(() => {
    // Note - adding event on window results into event capturing.
    if (keypressEnabled && hasMultipleSlides) document.addEventListener("keydown", changeChild);

    return () => {
      if (keypressEnabled && hasMultipleSlides)
        document.removeEventListener("keydown", changeChild);
    };
  });

  const changeSlide = (pos: any, action = "") => {
    switch (action) {
      case "increment":
        setActiveSlide((prev) => prev + 1);
        break;
      case "decrement":
        setActiveSlide((prev) => prev - 1);
        break;
      default:
        setActiveSlide(pos);
        break;
    }
    if (handleChangeSlide) handleChangeSlide(pos);
  };

  const slideRight = () => {
    setIsTransition(true);
    changeSlide(activeSlide + 1, "increment");

    if (activeSlide === totalSlide) {
      if (noInfinite) {
        changeSlide(1);
      } else {
        isWrapping.current = true;
        setTimeout(() => {
          setIsTransition(false);
          changeSlide(1);
          isWrapping.current = false;
        }, 500);
      }
    }
  };

  const slideLeft = () => {
    setIsTransition(true);
    changeSlide(activeSlide - 1, "decrement");
    if (activeSlide === 1) {
      if (noInfinite) {
        changeSlide(totalSlide);
      } else {
        isWrapping.current = true;
        setTimeout(() => {
          setIsTransition(false);
          changeSlide(totalSlide);
          isWrapping.current = false;
        }, 500);
      }
    }
  };

  const updatePosition = (clientX: any, clientY: any) => {
    const xDist = dragStartX.current - clientX;
    const yDist = dragStartY.current - clientY;
    const r = Math.atan2(yDist, xDist);
    let swipeAngle = Math.round((r * 180) / Math.PI);

    if (swipeAngle < 0) {
      swipeAngle = 360 - Math.abs(swipeAngle);
    }

    if (slideContainerRef.current) {
      const diffPer = (Math.abs(xDist) / slideContainerRef.current.offsetWidth) * 100;
      if (shouldStartTransitioningRef.current === "init") {
        if (Math.abs(swipeAngle - 90) <= 30 || Math.abs(swipeAngle - 270) <= 30) {
          shouldStartTransitioningRef.current = "blocked";
        } else {
          shouldStartTransitioningRef.current = "started";
        }
      }

      if (swipeAngle >= 50 && swipeAngle <= 300) {
        if (disableEdgeScrolling && activeSlide === 1) {
          return;
        }
        diffPercentageRef.current = diffPer;
        if (shouldStartTransitioningRef.current === "started") {
          slideContainerRef.current.style.transform = `translateX(-${
            activeSlide * 100 - diffPer
          }%)`;
        }
      }
      if ((swipeAngle <= 45 && swipeAngle >= 0) || (swipeAngle <= 360 && swipeAngle >= 315)) {
        if (disableEdgeScrolling && activeSlide === totalSlide) {
          return;
        }
        diffPercentageRef.current = diffPer;
        if (shouldStartTransitioningRef.current === "started") {
          slideContainerRef.current.style.transform = `translateX(-${
            activeSlide * 100 + diffPer
          }%)`;
        }
      }
    }
  };

  const onTouchMove = (evt: any) => {
    const touch = evt.targetTouches[0];
    const pos = touch.clientX - dragStartX.current;
    posRef.current = pos;

    updatePosition(touch.clientX, touch.clientY);
  };

  const onMouseMove = (e: any) => {
    if (isMouseDown.current) {
      const touch = e;
      const pos = touch.clientX - dragStartX.current;
      posRef.current = pos;

      updatePosition(touch.clientX, touch.clientY);
    }
  };

  const onDragStartTouch = (e: any) => {
    dragStartX.current = e.targetTouches[0].clientX;
    dragStartY.current = e.targetTouches[0].clientY;
    shouldStartTransitioningRef.current = "init";
    diffPercentageRef.current = 0;
    if (autoSlide) {
      // reset auto slide counter when user touch
      secondCountRef.current = 0;
      if (timerId.current) {
        clearInterval(timerId.current);
      }
    }
  };
  const onMouseDown = (e: any) => {
    isMouseDown.current = true;
    dragStartX.current = e.clientX;
    dragStartY.current = e.clientY;
    diffPercentageRef.current = 0;

    if (autoSlide) {
      // reset auto slide counter when user touch
      secondCountRef.current = 0;
      if (timerId.current) {
        clearInterval(timerId.current);
      }
    }
  };

  const onMouseLeave = () => {
    if (!isDragEndStart.current && isMouseDown.current) {
      onDragEnd();
    }
    diffPercentageRef.current = 0;
    isMouseDown.current = false;
  };

  const onDragEnd = () => {
    isMouseDown.current = false;
    isDragEndStart.current = true;
    if (shouldStartTransitioningRef.current !== "started") return;
    if (posRef.current > 0) {
      if (diffPercentageRef.current > threshold && (!noInfinite || activePos !== 1)) {
        slideLeft();
      } else {
        if (slideContainerRef.current) {
          slideContainerRef.current.classList.add(styles.sliderTransition);
          slideContainerRef.current.style.transform = `translateX(-${activeSlide * 100}%)`;
        }
        if (autoSlide) {
          trackTimeForAutoSlide();
        }
      }
    } else if (diffPercentageRef.current > threshold && (activePos !== totalSlide || !noInfinite)) {
      slideRight();
    } else {
      if (slideContainerRef.current) {
        slideContainerRef.current.classList.add(styles.sliderTransition);
        slideContainerRef.current.style.transform = `translateX(-${activeSlide * 100}%)`;
      }
      if (autoSlide) {
        trackTimeForAutoSlide();
      }
    }

    setTimeout(() => {
      setIsTransition(false);
      isDragEndStart.current = false;
    }, 500);
  };

  const handleLeftClick = (e: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    slideLeft();
  };

  const handleRightClick = (e: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    slideRight();
  };

  const trackTimeForAutoSlide = () => {
    if (timerId.current) {
      clearInterval(timerId.current);
    }
    secondCountRef.current = 0;
    timerId.current = setInterval(() => {
      secondCountRef.current += 1;
      if (
        secondCountRef.current ===
        (activeSlide === activePos && firstDelay && !autoSlideStarted.current ? firstDelay : delays)
      ) {
        autoSlideStarted.current = true;
        clearInterval(timerId.current);
        slideRight();
        secondCountRef.current = 0;
      }
    }, 1000);
  };

  if (!totalSlide) {
    return <div />;
  }

  return (
    <div
      ref={carouselRef}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
      className={formatClassNames(
        styles.carousel,
        className,
        roundedCorner && styles.roundedCorner,
        showNavOnHover && styles.showNavOnHover,
      )}
      style={style}
    >
      <div
        className={formatClassNames(
          showPartialSlidesOnEdge ? styles.sliderContainerReducedWidth : styles.sliderContainer,
          sliderWrapperClassName,
        )}
        role="none"
        onTouchStart={hasMultipleSlides ? onDragStartTouch : undefined}
        onTouchEnd={hasMultipleSlides ? onDragEnd : undefined}
        onMouseDown={hasMultipleSlides && isDraggableInDesktop ? onMouseDown : undefined}
        onMouseMove={hasMultipleSlides && isDraggableInDesktop ? onMouseMove : undefined}
        onMouseUp={hasMultipleSlides && isDraggableInDesktop ? onDragEnd : undefined}
        onMouseLeave={hasMultipleSlides && isDraggableInDesktop ? onMouseLeave : undefined}
        onTouchMove={hasMultipleSlides ? onTouchMove : undefined}
      >
        <div
          className={formatClassNames(
            styles.slider,
            isTransition && styles.sliderTransition,
            noInfinite && !autoSlide && styles.noInfiniteView,
            activePos === totalSlide &&
              totalSlide !== 1 &&
              noInfinite &&
              (lastClassName || styles.last),
            sliderContainerClassName && sliderContainerClassName,
            totalSlide === 1 && noInfinite && styles.onlyOne,
            overflowHide && styles.overflowHide,
            showModal && styles.noTransform,
          )}
          ref={slideContainerRef}
          style={activeSlide !== 1 ? { transform: `translateX(-${activeSlide * 100}%)` } : {}}
        >
          <div className={formatClassNames(styles.slide, slideClassName)}>
            {!noInfinite && slideList[totalSlide - 1]}
          </div>
          {slideList.map((item: any, index: number) => {
            const Slide = item;
            if (
              noLazyLoad ||
              autoSlide ||
              index === activePos ||
              index + 1 === activePos ||
              index + 2 === activePos ||
              // render the next 2 more slides to avoid lagging if this prop is true
              (renderMultipleNextSlide && index <= activePos + 2)
            ) {
              return (
                <div
                  key={index}
                  className={formatClassNames(styles.slide, slideClassName)}
                  data-label={
                    activeSlideDataLabel && index + 1 === activePos ? activeSlideDataLabel : ""
                  }
                >
                  {Slide}
                </div>
              );
            }
            return <div key={index} className={formatClassNames(styles.slide, slideClassName)} />;
          })}
          <div className={formatClassNames(styles.slide, slideClassName)}>
            {!noInfinite && slideList[0]}
          </div>
        </div>
      </div>
      {shouldShowNav && (
        <div className={formatClassNames(styles.navActionWrapper)}>
          <div
            data-testid="property-gallery-left-carousel"
            role="none"
            className={formatClassNames(
              styles.leftArrow,
              navSliderColor && styles[navSliderColor],
              leftNavSliderClassname,
            )}
            onClick={handleLeftClick}
          >
            {customArrowIcon || (
              <ArrowIcon
                width={arrowSize[navSliderSize]}
                height={arrowSize[navSliderSize]}
                stroke={arrowColor || "#fff"}
                strokeWidth={arrowStrokeWidth}
                {...(leftArrowClassName ? { className: leftArrowClassName } : {})}
              />
            )}
          </div>
          <div
            data-testid="property-gallery-right-carousel"
            role="none"
            className={formatClassNames(
              styles.rightArrow,
              navSliderColor && styles[navSliderColor],
              rightNavSliderClassname,
            )}
            onClick={handleRightClick}
          >
            {customArrowIcon || (
              <ArrowIcon
                width={arrowSize[navSliderSize]}
                height={arrowSize[navSliderSize]}
                stroke={arrowColor || "#fff"}
                strokeWidth={arrowStrokeWidth}
                {...(rightArrowClassName ? { className: rightArrowClassName } : {})}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

Carousel.defaultProps = {
  activeSlideDataLabel: "",
  className: "",
  autoSlide: false,
  showNavSlider: false,
  sliderContainerClassName: "",
  noInfinite: false,
  roundedCorner: false,
  navSliderSize: "small",
  navSliderColor: "",
  showNavOnHover: false,
  overflowHide: false,
  isDraggableInDesktop: false,
  delay: 0,
  leftNavSliderClassname: "",
  rightNavSliderClassname: "",
  lastClassName: "",
  carouselRef: null,
  slideClassName: "",
  noLazyLoad: false,
  firstDelay: 0,
  arrowColor: "",
  pause: false,
  showModal: false,
  renderMultipleNextSlide: false,
  disableEdgeScrolling: false,
  leftArrowClassName: "",
  rightArrowClassName: "",
  arrowStrokeWidth: 2,
  keypressEnabled: false,
};

export default wrapperHOC(Carousel, {
  componentName: "Carousel-Global",
  showForChina: true,
});
